import { cva, type VariantProps } from 'class-variance-authority';
import { Check, X } from 'lucide-react';
import {
  PASSWORD_HAS_DIGIT,
  PASSWORD_HAS_LENGTH,
  PASSWORD_HAS_UPPERCASE,
} from '@/lib/password-rules';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

/**
 * Live ✓/✗ checklist for the 3 password rules enforced by the
 * backend (Briefing Ch 6 line 508-511, aligned across SignupDto +
 * ResetPasswordDto + ChangePasswordDto in Batch 9B Commit 0):
 *
 *   - Mind. 8 Zeichen
 *   - Mind. 1 Großbuchstabe
 *   - Mind. 1 Zahl
 *
 * Built as a sibling to `PasswordStrengthMeter` rather than extending
 * it — Signup + Reset routes consume the meter today and don't need
 * the checklist; A-13 PasswordRoute is the only consumer that needs
 * both. Promote to a single combined component if/when Signup or
 * Reset adopts the checklist.
 *
 * `tone` cva variant matches InfoTooltip.tsx / ChildProfileFields.tsx
 * convention exactly — `'dark'` for components placed on a navy
 * backdrop (Signup, Reset under AuthLayout), `'light'` for the
 * white-card backdrop on Account/Password. Default is `'dark'` per
 * the convention; light-bg consumers MUST pass `tone="light"`
 * explicitly or text disappears against the white card.
 */
const xIconVariants = cva('h-3.5 w-3.5', {
  variants: {
    tone: {
      dark: 'text-white/40',
      light: 'text-dream-navy/40',
    },
    // Sebastian ruling 2026-09-23 — LightGateOverlay (bg-dream-white,
    // registration step before the story screen) needs unmet rules
    // in red so the customer sees WHICH rule is still open, without
    // waiting for a submit-error. `'muted'` (default) keeps Signup /
    // ResetPassword / Account/Password unchanged; `'error'` swaps
    // both the X-icon and the pending label to `text-error` (same
    // `--color-error: #e24b4a` token that drives the PersonenEditForm
    // "Es fehlen noch …" line).
    pendingTone: {
      muted: '',
      error: 'text-error',
    },
  },
  defaultVariants: { tone: 'dark', pendingTone: 'muted' },
});

const labelOkVariants = cva('', {
  variants: {
    tone: {
      dark: 'text-dream-white',
      light: 'text-dream-navy',
    },
  },
  defaultVariants: { tone: 'dark' },
});

const labelPendingVariants = cva('', {
  variants: {
    tone: {
      dark: 'text-dream-white/70',
      light: 'text-dream-navy/60',
    },
    pendingTone: {
      muted: '',
      error: 'text-error',
    },
  },
  defaultVariants: { tone: 'dark', pendingTone: 'muted' },
});

interface Props extends VariantProps<typeof xIconVariants> {
  password: string;
  className?: string;
}

export function PasswordRequirementsChecklist({ password, className, tone, pendingTone }: Props) {
  const labels = STRINGS.account.password.requirements;
  const items: Array<{ ok: boolean; label: string }> = [
    { ok: PASSWORD_HAS_LENGTH.test(password), label: labels.minLength },
    { ok: PASSWORD_HAS_UPPERCASE.test(password), label: labels.uppercase },
    { ok: PASSWORD_HAS_DIGIT.test(password), label: labels.digit },
  ];

  return (
    <ul
      className={cn('mt-2 space-y-1', className)}
      data-testid="password-requirements-checklist"
      aria-live="polite"
    >
      {items.map(({ ok, label }) => (
        <li key={label} className="flex items-center gap-1.5 text-xs">
          {ok ? (
            <Check
              className="h-3.5 w-3.5 text-[color:var(--color-success)]"
              strokeWidth={3}
              aria-hidden="true"
            />
          ) : (
            <X
              className={cn(xIconVariants({ tone, pendingTone }))}
              strokeWidth={3}
              aria-hidden="true"
            />
          )}
          <span
            className={
              ok ? cn(labelOkVariants({ tone })) : cn(labelPendingVariants({ tone, pendingTone }))
            }
          >
            {label}
          </span>
        </li>
      ))}
    </ul>
  );
}
